import { formatDateTime, formatDuration } from "./formatters";

const STRUCTURED_SUMMARY_KEYS = [
  "intent",
  "sentiment",
  "outcome",
  "outcome_detail",
  "outcomeDetail",
  "key_points",
  "keyPoints",
  "follow_up",
  "followUp",
  "follow_up_required",
  "followUpRequired",
  "follow_up_date",
  "followUpDate",
  "follow_up_notes",
  "followUpNotes",
  "escalation_needed",
  "escalationNeeded",
];

function pickValue(source = {}, keys = []) {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== "") {
      return source[key];
    }
  }

  return null;
}

function humanizeLabel(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function toParsableText(value) {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

function toText(value, seen = new WeakSet()) {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item, seen))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "";
    }

    seen.add(value);

    const preferredText = toText(
      pickValue(value, [
        "summary",
        "summary_text",
        "text",
        "label",
        "title",
        "name",
        "value",
        "detail",
        "description",
        "content",
        "outcome_detail",
        "outcomeDetail",
      ]),
      seen
    );

    if (preferredText) {
      return preferredText;
    }

    const parts = Object.entries(value)
      .map(([key, entryValue]) => {
        const entryText = toText(entryValue, seen);

        if (!entryText) {
          return "";
        }

        return `${humanizeLabel(key)}: ${entryText}`;
      })
      .filter(Boolean);

    return parts.join("; ");
  }

  return String(value);
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item))
      .filter(Boolean);
  }

  const text = toText(value);
  return text ? [text] : [];
}

function toBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }

    if (value === 0) {
      return false;
    }
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "yes", "y", "1"].includes(normalized)) {
      return true;
    }

    if (["false", "no", "n", "0"].includes(normalized)) {
      return false;
    }
  }

  return null;
}

function isStructuredSummaryObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return Object.keys(value).some((key) => STRUCTURED_SUMMARY_KEYS.includes(key));
}

function extractJsonLikeField(raw, fieldName) {
  if (!raw || typeof raw !== "string") {
    return "";
  }

  const pattern = new RegExp(`"${fieldName}"\\s*:\\s*"([\\s\\S]*?)(?:"\\s*,\\s*"|"$)`);
  const match = raw.match(pattern);

  if (!match?.[1]) {
    return "";
  }

  return match[1]
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "")
    .replace(/\\\\/g, "\\")
    .trim();
}

function extractJsonLikeArray(raw, fieldName) {
  if (!raw || typeof raw !== "string") {
    return [];
  }

  const pattern = new RegExp(`"${fieldName}"\\s*:\\s*\\[([\\s\\S]*?)\\]`);
  const match = raw.match(pattern);

  if (!match?.[1]) {
    return [];
  }

  return Array.from(match[1].matchAll(/"([\s\S]*?)"/g))
    .map((item) =>
      item[1]
        .replace(/\\"/g, '"')
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "")
        .replace(/\\\\/g, "\\")
        .trim()
    )
    .filter(Boolean);
}

function buildSummaryFallback(rawText) {
  if (rawText.startsWith("{") && rawText.includes('"summary"')) {
    return {
      summary: extractJsonLikeField(rawText, "summary") || "Summary details available.",
      intent: extractJsonLikeField(rawText, "intent"),
      sentiment: extractJsonLikeField(rawText, "sentiment"),
      outcome: extractJsonLikeField(rawText, "outcome"),
      outcome_detail: extractJsonLikeField(rawText, "outcome_detail"),
      key_points: extractJsonLikeArray(rawText, "key_points"),
      follow_up: extractJsonLikeArray(rawText, "follow_up"),
      follow_up_date: extractJsonLikeField(rawText, "follow_up_date"),
      follow_up_notes: extractJsonLikeField(rawText, "follow_up_notes"),
    };
  }

  return { summary: rawText };
}

export function parseCallSummary(summary) {
  if (!summary) {
    return null;
  }

  if (typeof summary === "object" && !Array.isArray(summary)) {
    if (Object.keys(summary).length === 1 && summary.summary !== undefined) {
      const nestedSummary = parseCallSummary(summary.summary);

      if (nestedSummary && typeof nestedSummary === "object" && !Array.isArray(nestedSummary)) {
        return nestedSummary;
      }
    }

    return summary;
  }

  const rawText = toParsableText(summary);

  if (!rawText) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawText);

    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      if (Object.keys(parsed).length === 1 && parsed.summary !== undefined) {
        const nestedSummary = parseCallSummary(parsed.summary);

        if (nestedSummary && typeof nestedSummary === "object" && !Array.isArray(nestedSummary)) {
          return nestedSummary;
        }
      }

      return parsed;
    }

    if (typeof parsed === "string") {
      const nestedSummary = parseCallSummary(parsed);
      return nestedSummary || { summary: parsed };
    }
  } catch (error) {
    return buildSummaryFallback(rawText);
  }

  return buildSummaryFallback(rawText);
}

export function safeParseJson(value) {
  if (!value) {
    return null;
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmedValue);

    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    return null;
  }

  return null;
}

export function getCallSummaryValue(record = {}) {
  const metadata = safeParseJson(record.metadata);

  return (
    record.summary_preview ||
    metadata?.summary_preview ||
    metadata?.summary ||
    record.summary ||
    record.latest_note ||
    record.notes ||
    record.reason ||
    null
  );
}

export function formatSummaryForDisplay(summary) {
  const parsed = parseCallSummary(summary);

  if (!parsed) {
    return {
      isEmpty: true,
      isStructured: false,
      summaryText: "",
      intent: "",
      sentiment: "",
      outcome: "",
      outcomeDetail: "",
      keyPoints: [],
      followUpItems: [],
      followUpRequired: false,
      followUpDate: "",
      followUpNotes: "",
      escalationNeeded: false,
      rawText: "",
    };
  }

  const rawText = typeof summary === "string" ? summary.trim() : toText(summary);
  const summaryText =
    toText(pickValue(parsed, ["summary", "summary_text"])) ||
    toText(pickValue(parsed, ["outcome_detail", "outcomeDetail"])) ||
    toText(pickValue(parsed, ["intent"])) ||
    rawText;
  const keyPoints = normalizeList(pickValue(parsed, ["key_points", "keyPoints"]));
  const followUpItems = normalizeList(pickValue(parsed, ["follow_up", "followUp"]));
  const followUpDate = toText(pickValue(parsed, ["follow_up_date", "followUpDate"]));
  const followUpNotes = toText(pickValue(parsed, ["follow_up_notes", "followUpNotes"]));
  const followUpRequiredValue = toBoolean(
    pickValue(parsed, ["follow_up_required", "followUpRequired"])
  );
  const escalationValue = toBoolean(
    pickValue(parsed, ["escalation_needed", "escalationNeeded"])
  );
  const inferredFollowUpRequired =
    followUpItems.length > 0 || Boolean(followUpDate) || Boolean(followUpNotes);

  return {
    isEmpty: false,
    isStructured: isStructuredSummaryObject(parsed),
    summaryText: summaryText || "No summary available",
    intent: toText(pickValue(parsed, ["intent"])),
    sentiment: toText(pickValue(parsed, ["sentiment"])),
    outcome: toText(pickValue(parsed, ["outcome"])),
    outcomeDetail: toText(pickValue(parsed, ["outcome_detail", "outcomeDetail"])),
    keyPoints,
    followUpItems,
    followUpRequired:
      typeof followUpRequiredValue === "boolean"
        ? followUpRequiredValue
        : inferredFollowUpRequired,
    hasFollowUpRequired:
      typeof followUpRequiredValue === "boolean" || inferredFollowUpRequired,
    followUpDate,
    followUpNotes,
    escalationNeeded: escalationValue,
    hasEscalationNeeded: typeof escalationValue === "boolean",
    rawText,
  };
}

export function getSummaryPreview(summary, maxLength = 100) {
  const formatted = formatSummaryForDisplay(summary);
  const text = formatted.summaryText || "No summary available";

  if (!text || text.startsWith('{"summary"')) {
    return "Summary details available";
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength).trim()}...`;
}

export function getReadableSummaryText(summary) {
  const formatted = formatSummaryForDisplay(summary);
  return formatted.summaryText || "No summary available";
}

export function hasTranscript(call = {}) {
  const transcript = call?.transcript;

  if (typeof transcript === "string") {
    return transcript.trim().length > 0;
  }

  return transcript != null;
}

function getTranscriptText(call = {}) {
  if (!hasTranscript(call)) {
    return "";
  }

  if (typeof call.transcript === "string") {
    return call.transcript.trim();
  }

  try {
    return JSON.stringify(call.transcript, null, 2);
  } catch (error) {
    return String(call.transcript);
  }
}

function sanitizeFilenamePart(value, fallback = "unknown") {
  const cleaned = Array.from(String(value || "").trim())
    .map((character) => {
      const charCode = character.charCodeAt(0);

      if (charCode < 32 || /[<>:"/\\|?*]/.test(character)) {
        return "-";
      }

      return character;
    })
    .join("")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return cleaned || fallback;
}

function getCreatedDateToken(call = {}) {
  const createdAt = String(call.created_at || "").trim();

  if (!createdAt) {
    return "unknown-date";
  }

  const directDate = createdAt.slice(0, 10).replace(/[^\d-]/g, "");

  if (directDate) {
    return directDate;
  }

  const parsed = new Date(createdAt);

  if (Number.isNaN(parsed.getTime())) {
    return "unknown-date";
  }

  return parsed.toISOString().slice(0, 10);
}

function buildTranscriptFileName(call = {}) {
  const callerPart = sanitizeFilenamePart(
    call.caller_name || call.phone_number || call.phone || "unknown"
  );
  const datePart = sanitizeFilenamePart(getCreatedDateToken(call), "unknown-date");

  return `call-transcript-${callerPart}-${datePart}.txt`;
}

function buildTranscriptFileContent(call = {}) {
  const summaryText = getReadableSummaryText(call.summary);
  const transcriptText = getTranscriptText(call);

  return [
    "Calling - Call Transcript",
    "",
    "Call Details",
    "------------",
    `Caller Name: ${call.caller_name || "N/A"}`,
    `Phone Number: ${call.phone_number || call.phone || "N/A"}`,
    `Call Direction: ${call.call_direction || call.direction || "N/A"}`,
    `Service Type: ${call.service_type || "N/A"}`,
    `Booking Status: ${call.booking_status || "N/A"}`,
    `Confirmation Number: ${call.confirmation_number || "N/A"}`,
    `Date & Time: ${formatDateTime(call.created_at)}`,
    `Duration: ${formatDuration(call.duration_seconds)}`,
    "",
    "Summary",
    "-------",
    summaryText,
    "",
    "Transcript",
    "----------",
    transcriptText,
    "",
  ].join("\n");
}

export function downloadTranscript(call = {}) {
  const transcriptText = getTranscriptText(call);

  if (!transcriptText) {
    return false;
  }

  const fileName = buildTranscriptFileName(call);
  const content = buildTranscriptFileContent(call);
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);

  return true;
}
